import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Grid, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Button } from '@mui/material';
import { printReport, currencyCol, textCol } from '../../utils/pdfPrint';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ReportPage, ExportMenu } from './ReportKit';

// Shkalla tatimore default (nëse mungojnë politikat fiskale në DB)
const DEFAULT_FISCAL_RANGES = [
  // Ligji Nr. 08/L-142 — pragu i liruar 250 €/muaj (nga rroga e shtatorit 2024).
  { from: 0, to: 250, tax: 0 },
  { from: 250, to: 450, tax: 8 },
  { from: 450, to: null, tax: 10 },
];

export default function MonthlyCostReport() {
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [rates, setRates] = useState({ employee: 0.05, employer: 0.05 });
  const [emailOpen, setEmailOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    apiService().get('salariesmonthlylists').then((r) => r?.success && setLists(r.data || []));
    apiService().get('workers').then((r) => r?.success && setWorkers(r.data || []));
    apiService().get('fiscalPolitics').then((r) => {
      const fp = r?.success && r.data?.length ? r.data[0] : null;
      if (fp) {
        const employeeRaw = Number(fp.EmployeeTax);
        const employerRaw = Number(fp.EmployerTax);
        const employee = Number.isFinite(employeeRaw) ? (employeeRaw > 1 ? employeeRaw / 100 : employeeRaw) : 0.05;
        const employer = Number.isFinite(employerRaw) ? (employerRaw > 1 ? employerRaw / 100 : employerRaw) : 0.05;
        setRates({ employee, employer });
        if (fp.Id) {
          apiService().query('getFiscalpoliticsDetails', `FiscalPoliticsId=${fp.Id}`).then((d) => {
            if (d?.success) setRanges(d.data?.length ? d.data : DEFAULT_FISCAL_RANGES);
          });
        }
      }
    });
  }, []);

  useEffect(() => { if(listId) apiService().query('getSalariesByListId',`id=${listId}`).then((r)=>r?.success&&setRows(r.data||[])); }, [listId]);

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const hasNumber = (v) => v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v));
  const asRate = (v, fallback) => {
    const n = Number(v);
    if (Number.isFinite(n)) return n > 1 ? n / 100 : Math.max(n, 0);
    return fallback;
  };
  const fullName = (r) => r?.FullName || r?.fullName || `${r?.FirstName || r?.firstname || ''} ${r?.LastName || r?.lastname || ''}`.trim();
  const getWorkerName = (r) => {
    const w = workers.find((x) => String(x.Id) === String(r.WorkerId || r.Employee || r.Id));
    if (w) return `${w.FirstName || ''} ${w.LastName || ''}`.trim() || w.FullName || w.Name || fullName(r);
    return fullName(r);
  };

  const computeTax = useCallback((taxable) => {
    const activeRanges = ranges.length ? ranges : DEFAULT_FISCAL_RANGES;
    if (!activeRanges.length || !Number.isFinite(taxable) || taxable <= 0) return 0;
    let total = 0;
    for (const r of activeRanges) {
      const from = toNumber(r.From ?? r.range_from ?? r.from);
      const to = r.To == null ? null : toNumber(r.To ?? r.range_to ?? r.to);
      const rate = asRate(r.Tax ?? r.tax ?? r.Rate ?? r.rate, 0);
      if (rate <= 0) continue;
      if (to === null) {
        if (taxable > from) total += (taxable - from) * rate;
      } else if (taxable > from) {
        total += (Math.min(taxable, to) - from) * rate;
      }
    }
    return +total.toFixed(2);
  }, [ranges]);

  const computed = useMemo(()=>{
    return rows.map((r)=>{
      const salary = toNumber(r.Salary);
      const overtime = toNumber(r.HourSalary);
      const bonus = toNumber(r.Bonus);
      const penalties = toNumber(r.Penalties);
      const advances = toNumber(r.Advances);
      const employerTaxFee = toNumber(r.employerTaxFee || r.EmployerTaxFee);
      const employeeRate = asRate(r.EmployeeTax ?? r.employeeTax, rates.employee);
      const employerRate = asRate(r.EmployerTax ?? r.employerTax, rates.employer);
      const employeeContribution = hasNumber(r.contributions) ? toNumber(r.contributions) : +(salary * employeeRate).toFixed(2);
      const employerContribution = +(salary * employerRate).toFixed(2);
      const gross = salary + overtime + bonus - penalties;
      const taxable = Math.max(gross - employeeContribution, 0);
      const taxes = hasNumber(r.taxes) ? toNumber(r.taxes) : computeTax(taxable);
      const net = hasNumber(r.neto) ? toNumber(r.neto) : +(taxable - taxes - advances).toFixed(2);
      const employerCost = +(employerContribution + employerTaxFee).toFixed(2);
      const companyCost = +(net + taxes + employeeContribution + employerCost).toFixed(2);
      return { ...r, name: getWorkerName(r), taxes, contributions: employeeContribution, employerContribution, employerCost, companyCost, neto: net };
    });
  },[rows, computeTax, rates.employee, rates.employer, workers]);

  const totals = useMemo(()=>{
    const sum=(k)=>computed.reduce((a,b)=>a+(Number(b[k])||0),0);
    return {
      neto: sum('neto'),
      taxes: sum('taxes'),
      contributions: sum('contributions'),
      employerCost: sum('employerCost'),
      companyCost: sum('companyCost'),
    };
  },[computed]);

  function fmt(n){ return new Intl.NumberFormat('sq-AL',{style:'currency',currency:'EUR'}).format(n||0); }
  const n2 = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '0.00');

  function exportCsv(){
    const headers=[t('report.worker'),t('report.net'),t('report.tax'),t('report.contributions'),t('report.employerCost'),t('report.totalCost')];
    const lines=[headers.join(',')];
    computed.forEach((r)=>{
      const line=[r.name||fullName(r), n2(r.neto), n2(r.taxes), n2(r.contributions), n2(r.employerCost), n2(r.companyCost)]
        .map((x)=>`"${String(x).replace(/"/g,'""')}"`).join(',');
      lines.push(line);
    });
    lines.push([t('report.total'), n2(totals.neto), n2(totals.taxes), n2(totals.contributions), n2(totals.employerCost), n2(totals.companyCost)].map((x)=>`"${x}"`).join(','));
    const csv=lines.join('\r\n');
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='monthly_costs.csv'; a.click(); URL.revokeObjectURL(url);
  }

  function exportXls(){
    const headers=[t('report.worker'),t('report.net'),t('report.tax'),t('report.contributions'),t('report.employerCost'),t('report.totalCost')];
    const style = `<style>
      table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
      th, td { border: 1px solid #d0d7de; padding: 6px 8px; text-align: right; }
      th:first-child, td:first-child { text-align: left; }
      th { background: #f0f4ff; }
      .totals td { font-weight: 700; background: #fafafa; }
    </style>`;
    const rowsHtml = computed.map(r=> `<tr><td>${r.name||''}</td><td>${n2(r.neto)}</td><td>${n2(r.taxes)}</td><td>${n2(r.contributions)}</td><td>${n2(r.employerCost)}</td><td>${n2(r.companyCost)}</td></tr>`).join('');
    const totalsRow = `<tr class="totals"><td>${t('report.total')}</td><td>${n2(totals.neto)}</td><td>${n2(totals.taxes)}</td><td>${n2(totals.contributions)}</td><td>${n2(totals.employerCost)}</td><td>${n2(totals.companyCost)}</td></tr>`;
    const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'>${style}</head><body><table><thead><tr>${headers.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rowsHtml}${totalsRow}</tbody></table></body></html>`;
    const blob=new Blob([html],{type:'application/vnd.ms-excel'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='monthly_costs.xls'; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const pdfRows = computed.map((r) => ({ ...r, _name: r.name || fullName(r) }));
    printReport({
      title: t('reports.monthlyCost'),
      subtitle: listId ? `${t('report.list')} #${listId}` : '',
      orientation: 'landscape',
      columns: [
        textCol(t('report.worker'), '_name', '28%'),
        currencyCol(t('report.net'), 'neto', '14%'),
        currencyCol(t('report.tax'), 'taxes', '13%'),
        currencyCol(t('report.contributions'), 'contributions', '15%'),
        currencyCol(t('report.employerCost'), 'employerCost', '15%'),
        currencyCol(t('report.totalCost'), 'companyCost', '15%'),
      ],
      rows: pdfRows,
      totals: { neto: totals.neto, taxes: totals.taxes, contributions: totals.contributions, employerCost: totals.employerCost, companyCost: totals.companyCost },
      badge: { label: `${computed.length} ${t('report.workers')}` },
    });
  }

  return (
    <ReportPage>
      <PageHeader title={t('reports.monthlyCost')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>} />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label={t('report.monthlyList')} value={listId} onChange={(e)=>setListId(e.target.value)}>
                {lists.map((l)=>(<MenuItem key={l.id} value={l.id}>{`${l.month}/${l.year}`}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={exportCsv} onExcel={exportXls} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={!listId || computed.length === 0} /></Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('report.worker')}</TableCell>
                <TableCell align="right">{t('report.net')}</TableCell>
                <TableCell align="right">{t('report.tax')}</TableCell>
                <TableCell align="right">{t('report.contributions')}</TableCell>
                <TableCell align="right">{t('report.employerCost')}</TableCell>
                <TableCell align="right">{t('report.totalCost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computed.map((r)=> (
                <TableRow key={r.Id}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{fmt(r.neto)}</TableCell>
                  <TableCell align="right">{fmt(r.taxes)}</TableCell>
                  <TableCell align="right">{fmt(r.contributions)}</TableCell>
                  <TableCell align="right">{fmt(r.employerCost)}</TableCell>
                  <TableCell align="right">{fmt(r.companyCost)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><b>{t('report.total')}</b></TableCell>
                <TableCell align="right"><b>{fmt(totals.neto)}</b></TableCell>
                <TableCell align="right"><b>{fmt(totals.taxes)}</b></TableCell>
                <TableCell align="right"><b>{fmt(totals.contributions)}</b></TableCell>
                <TableCell align="right"><b>{fmt(totals.employerCost)}</b></TableCell>
                <TableCell align="right"><b>{fmt(totals.companyCost)}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.monthlyCost')}
        subtitle={listId ? `${t('report.list')} #${listId}` : ''}
        columns={[
          { label: t('report.worker'), key: 'name', align: 'left' },
          { label: t('report.net'), key: 'neto', align: 'right', format: 'currency' },
          { label: t('report.tax'), key: 'taxes', align: 'right', format: 'currency' },
          { label: t('report.contributions'), key: 'contributions', align: 'right', format: 'currency' },
          { label: t('report.employerCost'), key: 'employerCost', align: 'right', format: 'currency' },
          { label: t('report.totalCost'), key: 'companyCost', align: 'right', format: 'currency' },
        ]}
        rows={computed}
        totals={totals}
        workers={workers}
      />
    </ReportPage>
  );
}
